import { RouteComponentProps, Route, Switch } from 'react-router-dom'
import { ApolloQueryResult } from '@apollo/client'
import React, { createContext, FC, useState } from 'react'
import { t } from '@lingui/macro'

import { Page } from '../../components/common/page/Page'
import PageHeader from '../../components/common/page/header/PageHeader'
import { TabsStatic } from '../../components/common/tabs/TabsLegacy'
import {
  CampaignNewSurveyShareList,
  CampaignSurveyShareList
} from './campaign-new-action/campaign-share-component/CampaignSurveyShareList'
import { CampaignOverview } from './campaign-overview/CampaignOverview'
import { CampaignAudience } from './components/campaign-audience/CampaignAudience'
import { CampaignActions } from './campaign-actions/CampaignActions'
import CampaignStatus from './components/campaign-status/CampaignStatus'
import IconSettings from '../../components/common/icon/IconSettings'
import IconButton from '../../components/common/form/button/IconButton'
import { ManageCampaignDrawer } from './manage-campaign-drawer/ManageCampaignDrawer'
import './ManageCampaignPage.less'
import IconEnergy from '../../components/common/icon/IconEnergy'
import IconPeople from '../../components/common/icon/IconPeople'
import IconList from '../../components/common/icon/IconList'
import { CampaignBannerComponent } from './campaign-new-action/campaign-banner-component/CampaignBannerComponent'
import { CampaignEmailComponent } from './campaign-new-action/campaign-email-component/CampaignEmailComponent'
import NotFound from '../../components/common/not-found/NotFound'
import CampaignShareComponent from './campaign-new-action/campaign-share-component/CampaignShareComponent'

import { translateActionType } from './campaign-overview/create-campaign-action-modal/CreateCampaignActionModal'
import {
  CampaignNewTopicShareList,
  CampaignTopicShareList
} from './campaign-new-action/campaign-share-component/CampaignTopicShareList'
import {
  CampaignNewPathwayShareList,
  CampaignPathwayShareList
} from './campaign-new-action/campaign-share-component/CampaignPathwayShareList'
import Loader from '../../components/common/loader/Loader'
import CampaignMessageComponent from './campaign-new-action/campaign-message-component/CampaignMessageComponent'
import { CampaignLinkReport } from './campaign-link-reports/CampaignLinkReport'
import { CampaignActionReport } from './campaign-action-reports/CampaignActionReport'

import IconGraph from '../../components/common/icon/IconGraph'
import { IconByCampaignType } from './utils'
import {
  CampaignCertificationShareList,
  CampaignNewCertificationShareList
} from './campaign-new-action/campaign-share-component/CampaignCertificationShareList'

import {
  CampaignDetailQuery,
  useCampaignDetailQuery
} from './ManageCampaignPage.codegen'
import { Box, Flex, Heading } from '@chakra-ui/react'
import { staticLinks } from 'shared/linkBuilder'
import { CampaignActionTypeEnum } from 'shared/enums/campaigns'

export interface IManageCampaignPageProps {
  match: { url: string; params: { campaignId: string } }
}

export type SingleCampaign = NonNullable<
  NonNullable<CampaignDetailQuery['admin']>['campaign']
>

interface IManageCampaignContext {
  campaign: SingleCampaign
  refetchCampaign: () => Promise<ApolloQueryResult<CampaignDetailQuery>>
  loading: boolean
}

export const ManageCampaignContext = createContext<IManageCampaignContext>(
  null as unknown as IManageCampaignContext
)

export interface ICampaignActionItemListProps {
  actionId: number
}

const ManageCampaignPage: FC<IManageCampaignPageProps> = ({
  match
}: IManageCampaignPageProps) => {
  const queryResult = useCampaignDetailQuery({
    // there were some issues with apollo client when this query is returned from the cache. Problem was only reproducible on prod. If this problem ever reappears, just revert https://github.com/LeapLabs/project-alpha/pull/3667
    variables: {
      id: Number(match.params.campaignId)
    }
  })

  const { data, refetch, loading } = queryResult

  const [showEditDrawer, setShowEditDrawer] = useState<boolean>(false)

  const renderContent = (campaign, url) => {
    const { pathname } = window.location

    const tabList = [
      {
        caption: t`Campaign summary`,
        icon: <IconList />,
        href: match.url
      },
      {
        caption: t`Audience`,
        icon: <IconPeople />,
        href: `${match.url}/audience`
      },
      {
        caption: t`Actions`,
        icon: <IconEnergy />,
        href: `${match.url}/actions`,
        isActive:
          pathname.includes(`/actions`) ||
          pathname.includes(`/new-email`) ||
          pathname.includes(`/new-banner`) ||
          pathname.includes(`/new-pathway-share`) ||
          pathname.includes(`/new-certification-share`) ||
          pathname.includes(`/new-topic-share`) ||
          pathname.includes(`/new-survey-share`) ||
          pathname.includes(`/email`) ||
          pathname.includes(`/banner`) ||
          pathname.includes(`/new-message`) ||
          pathname.includes(`/message`)
      },
      {
        caption: t`Action reports`,
        icon: <IconGraph />,
        href: `${match.url}/reports`
      },
      {
        caption: t`Link reports`,
        icon: <IconGraph />,
        href: `${match.url}/link-reports`
      }
    ]

    return (
      <ManageCampaignContext.Provider
        value={{ campaign, refetchCampaign: refetch, loading }}
      >
        <PageHeader
          className="PageHeader"
          defaultBackButtonPath={staticLinks.campaigns}
          title={
            <Flex>
              <Box m="0 6px" className="PageHeader__icon">
                <IconByCampaignType
                  withTooltip={true}
                  scheduleType={campaign.schedule_type}
                />
              </Box>
              <Heading size="xl" fontWeight={500} m="0 6px">
                {campaign.name}
              </Heading>
            </Flex>
          }
          titleRawText={campaign.name}
          titleFollowers={
            <Flex flexDirection="row">
              <CampaignStatus status={campaign.status} />
              <IconButton
                icon={<IconSettings />}
                hint={t`Settings`}
                color="medium"
                onClick={() => {
                  setShowEditDrawer(true)
                }}
              />
            </Flex>
          }
        />
        <TabsStatic tabList={tabList}>
          <Switch>
            <Route path={`${url}/audience`} component={CampaignAudience} />
            <Route path={`${url}/actions`} component={CampaignActions} />
            <Route
              path={`${url}/new-email`}
              component={CampaignEmailComponent}
            />
            <Route
              path={`${url}/new-manager-notification`}
              component={CampaignEmailComponent}
            />
            <Route
              path={`${url}/new-banner`}
              component={CampaignBannerComponent}
            />

            <Route
              path={`${url}/new-topic-share`}
              render={({ location }: RouteComponentProps) => {
                return (
                  <CampaignShareComponent
                    routeLocation={location}
                    type={CampaignActionTypeEnum.TOPIC_SHARE_ACTION}
                    title={translateActionType(
                      CampaignActionTypeEnum.TOPIC_SHARE_ACTION
                    )}
                    itemList={<CampaignNewTopicShareList />}
                  />
                )
              }}
            />

            <Route
              path={`${url}/new-pathway-share`}
              render={({ location }: RouteComponentProps) => {
                return (
                  <CampaignShareComponent
                    routeLocation={location}
                    type={CampaignActionTypeEnum.PATHWAY_SHARE_ACTION}
                    title={translateActionType(
                      CampaignActionTypeEnum.PATHWAY_SHARE_ACTION
                    )}
                    itemList={<CampaignNewPathwayShareList />}
                  />
                )
              }}
            />

            <Route
              path={`${url}/new-certification-share`}
              render={({ location }: RouteComponentProps) => {
                return (
                  <CampaignShareComponent
                    routeLocation={location}
                    type={CampaignActionTypeEnum.CERTIFICATION_SHARE_ACTION}
                    title={translateActionType(
                      CampaignActionTypeEnum.CERTIFICATION_SHARE_ACTION
                    )}
                    itemList={<CampaignNewCertificationShareList />}
                  />
                )
              }}
            />

            <Route
              path={`${url}/new-survey-share`}
              render={({ location }: RouteComponentProps) => {
                return (
                  <CampaignShareComponent
                    routeLocation={location}
                    type={CampaignActionTypeEnum.SURVEY_SHARE_ACTION}
                    title={translateActionType(
                      CampaignActionTypeEnum.SURVEY_SHARE_ACTION
                    )}
                    itemList={<CampaignNewSurveyShareList />}
                  />
                )
              }}
            />

            <Route
              path={`${url}/new-message`}
              component={CampaignMessageComponent}
            />

            <Route
              path={`${url}/reports`}
              render={() => {
                return <CampaignActionReport />
              }}
            />
            <Route
              path={`${url}/link-reports`}
              render={() => {
                return <CampaignLinkReport />
              }}
            />
            <Route
              path={`${url}/email/:emailId`}
              render={({
                match
              }: {
                match: { params: { emailId: string } }
              }) => {
                const campaignEmail = campaign.emailActions.find(
                  (email) => email.id === +match.params.emailId
                )
                if (!campaignEmail) {
                  return (
                    <NotFound
                      message={t`Send email campaign action ID  ${match.params.emailId} not found`}
                    />
                  )
                }
                return <CampaignEmailComponent email={campaignEmail} />
              }}
            />
            <Route
              path={`${url}/banner/:bannerId`}
              render={({
                match
              }: {
                match: { params: { bannerId: string } }
              }) => {
                const campaignBanner = campaign.banners.find(
                  (banner) => banner.id === +match.params.bannerId
                )
                if (!campaignBanner) {
                  return (
                    <NotFound
                      message={t`Share banner campaign action ID ${match.params.bannerId} not found`}
                    />
                  )
                }
                return <CampaignBannerComponent banner={campaignBanner} />
              }}
            />
            <Route
              path={`${url}/topic-share/:topicId`}
              render={({
                match
              }: {
                match: { params: { topicId: string } }
              }) => {
                const campaignTopic = campaign.topicShareActions.find(
                  (topic) => topic.id === +match.params.topicId
                )
                if (!campaignTopic) {
                  return (
                    <NotFound
                      message={t`Share resource campaign action ID ${match.params.topicId} not found`}
                    />
                  )
                }
                return (
                  <CampaignShareComponent
                    shareId={+match.params.topicId}
                    type={CampaignActionTypeEnum.TOPIC_SHARE_ACTION}
                    title={translateActionType(
                      CampaignActionTypeEnum.TOPIC_SHARE_ACTION
                    )}
                    itemList={
                      <CampaignTopicShareList
                        actionId={+match.params.topicId}
                      />
                    }
                  />
                )
              }}
            />
            <Route
              path={`${url}/pathway-share/:pathwayId`}
              render={({
                match
              }: {
                match: { params: { pathwayId: string } }
              }) => {
                const campaignPathway = campaign.pathwayShareActions.find(
                  (pathway) => pathway.id === +match.params.pathwayId
                )
                if (!campaignPathway) {
                  return (
                    <NotFound
                      message={t`Share pathway campaign action ID ${match.params.pathwayId} not found`}
                    />
                  )
                }
                return (
                  <CampaignShareComponent
                    shareId={+match.params.pathwayId}
                    type={CampaignActionTypeEnum.PATHWAY_SHARE_ACTION}
                    title={translateActionType(
                      CampaignActionTypeEnum.PATHWAY_SHARE_ACTION
                    )}
                    itemList={
                      <CampaignPathwayShareList
                        actionId={+match.params.pathwayId}
                      />
                    }
                  />
                )
              }}
            />
            <Route
              path={`${url}/certification-share/:shareActionId`}
              render={({
                match
              }: {
                match: { params: { shareActionId: string } }
              }) => {
                const campaignCertification =
                  campaign.certificationShareActions.find(
                    (certification) =>
                      certification.id === +match.params.shareActionId
                  )
                if (!campaignCertification) {
                  return (
                    <NotFound
                      message={t`Share certification campaign action ID ${match.params.shareActionId} not found`}
                    />
                  )
                }
                return (
                  <CampaignShareComponent
                    shareId={+match.params.shareActionId}
                    type={CampaignActionTypeEnum.CERTIFICATION_SHARE_ACTION}
                    title={translateActionType(
                      CampaignActionTypeEnum.CERTIFICATION_SHARE_ACTION
                    )}
                    itemList={
                      <CampaignCertificationShareList
                        actionId={+match.params.shareActionId}
                      />
                    }
                  />
                )
              }}
            />
            <Route
              path={`${url}/survey-share/:surveyId`}
              render={({
                match
              }: {
                match: { params: { surveyId: string } }
              }) => {
                const campaignSurvey = campaign.surveyShareActions.find(
                  (survey) => survey.id === +match.params.surveyId
                )
                if (!campaignSurvey) {
                  return (
                    <NotFound
                      message={t`Share survey campaign action ID ${match.params.surveyId} not found`}
                    />
                  )
                }
                return (
                  <CampaignShareComponent
                    shareId={+match.params.surveyId}
                    type={CampaignActionTypeEnum.SURVEY_SHARE_ACTION}
                    title={translateActionType(
                      CampaignActionTypeEnum.SURVEY_SHARE_ACTION
                    )}
                    itemList={
                      <CampaignSurveyShareList
                        actionId={+match.params.surveyId}
                      />
                    }
                  />
                )
              }}
            />
            <Route
              path={`${url}/message/:messageId`}
              render={({
                match
              }: {
                match: { params: { messageId: string } }
              }) => {
                const campaignMessage = campaign.messageActions.find(
                  (message) => message.id === +match.params.messageId
                )
                if (!campaignMessage) {
                  return (
                    <NotFound
                      message={t`Send message campaign action ID ${match.params.messageId} not found`}
                    />
                  )
                }
                return <CampaignMessageComponent message={campaignMessage} />
              }}
            />
            <Route render={() => <CampaignOverview />} />
          </Switch>
        </TabsStatic>

        <ManageCampaignDrawer
          show={showEditDrawer}
          onHide={() => {
            setShowEditDrawer(false)
          }}
          onUpdate={() => {
            refetch()
          }}
        />
      </ManageCampaignContext.Provider>
    )
  }

  const campaign = data?.admin?.campaign

  return (
    <Page className="ManageCampaignPage">
      {campaign ? renderContent(campaign, match.url) : <Loader />}
    </Page>
  )
}

export default ManageCampaignPage
